import React, { useContext, useEffect, useState, FC } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { VerticalContext } from 'store/verticals/Context'
import ActivityOverviewCard from 'containers/VerticalPage/common/ActivityOverviewCard/ActivityOverviewCard'
import VerticalCard from 'containers/VerticalPage/common/VerticalCard/VerticalCard'
import RevenueSourcesOverview from 'containers/VerticalPage/common/RevenueSourcesOverview/RevenueSourcesOverview'
import EntityCard from 'components/EntityCard/EntityCard'
import GenericCarousel from 'components/common/GenericCarousel'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import {
  ActivityOverviewCardLoader,
  EntityCardLoader,
  VerticalCarouselLoader,
} from 'containers/VerticalPage/common/Loaders'
import { selectCurrentZone } from '../../../../store/global'
import type { CurrentZone } from 'utils/types'
import { PLATFORMS, RevenueSource } from 'store/verticals/types'

const eyeSlashImage = require('containers/VerticalPage/influencers/assets/eye-slash.png')

const Content: FC = () => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)
  const { entityId } = useParams<{ entityId: string }>()
  const { getVerticalEntity, verticalEntity } = useContext(VerticalContext)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!entityId || !currentZone) return

    if (currentZone.influencersIndices) {
      const fetchPersonCardData = async () => {
        setIsLoading(true)
        await getVerticalEntity(entityId, currentZone.influencersIndices)
        setIsLoading(false)
      }
      fetchPersonCardData()
    }
  }, [currentZone, entityId])

  const preProcessRevenueSources = (sources: RevenueSource[] = []): RevenueSource[] => {
    return sources.map(item => {
      if (item.platform === PLATFORMS.ONLYFANS) {
        return {
          ...item,
          profileImageUrl: eyeSlashImage,
        }
      }
      return item
    })
  }

  const getIncludedFields = (platform: PLATFORMS): string[] | undefined => {
    if (platform === PLATFORMS.ONLYFANS) {
      return ['firstActivity', 'media', 'subscription_price', 'likes']
    }
  }

  const renderCarouselCards = () => {
    const revenueSources = preProcessRevenueSources(verticalEntity?.revenueSources)

    return revenueSources?.length ? (
      revenueSources?.map(source => (
        <Stack
          direction='row'
          key={source.profileUrl}
          sx={{
            justifyContent: 'space-between',
            mb: '0',
          }}
        >
          <VerticalCard
            platform={source.platform}
            profileName={source.profileName}
            profileImage={source.profileImageUrl}
            profileUrl={source.profileUrl}
          >
            <RevenueSourcesOverview
              revenueSources={source.overview}
              includeFields={getIncludedFields(source.platform)}
              currentSource={source}
            />
          </VerticalCard>
        </Stack>
      ))
    ) : (
      <VerticalCarouselLoader />
    )
  }

  return (
    <Box
      sx={{
        gridColumn: '1 / 2',
        gridRow: '1 / 2',
        display: 'grid',
        gridTemplateColumns: '32% minmax(0, 1fr)',
        gridTemplateRows: 'auto',
        gap: { md: '12px 18px', lg: '12px 22px', xl: '18px 25px', xxl: '28px', xxxl: '30px' },
      }}
    >
      {verticalEntity?.entityDetails ? (
        <EntityCard
          tin={verticalEntity.entityDetails.tin}
          name={verticalEntity.entityDetails.name}
          image={verticalEntity.entityDetails.image}
          profileUrls={verticalEntity.entityDetails.profileUrls}
          location={verticalEntity.entityDetails.location}
          birthday={verticalEntity.entityDetails.birthday}
        />
      ) : (
        isLoading && <EntityCardLoader />
      )}

      {verticalEntity?.overviewDetails ? (
        <ActivityOverviewCard
          overviewData={verticalEntity.overviewDetails}
          revenueSources={verticalEntity.revenueSources}
        />
      ) : (
        isLoading && <ActivityOverviewCardLoader />
      )}

      {verticalEntity ? (
        <GenericCarousel slidesCount={verticalEntity?.revenueSources?.length || 0}>
          {renderCarouselCards()}
        </GenericCarousel>
      ) : (
        isLoading && <VerticalCarouselLoader />
      )}
    </Box>
  )
}

export default Content
